import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaTimes, FaBars, FaStar, FaMapMarkerAlt, 
  FaArrowRight, FaCheck,
  FaFacebookF, FaTwitter, FaLinkedinIn, FaInstagram,
  FaCheckCircle, FaApple, FaGooglePlay, 
  FaTools, FaFileInvoiceDollar, FaUsers, FaChartLine, FaMobileAlt
} from 'react-icons/fa';


import { FcGoogle } from 'react-icons/fc'

import { ShieldCheck } from "lucide-react";

// 1. IMPORT  COMPONENTS
import SocialProofEngine from '../../components/SocialProof/SocialProofEngine'; 
import AiSearchAssistant from "../../components/AiSearchAssistant/AiSearchAssistant";
import AvailableJobs from '../../components/LiveJobs/AvailableJobs';

import AppDownloadSection from './components/AppDownloadSection'; 
import TestimonialsSection from './components/TestimonialsSection'; 
import FinalCtaSection from './components/FinalCtaSection'; 

// 2. IMPORT STYLES & ASSETS
import './BusinessGrowthPageNew.css';
import styles from './LandingPage.module.css';
import imageLogo from "../../assets/images/BSPLogoTranSmall.png";
import businessHeroImage from '../../assets/images/business_hero.jpg'; 
import workersImage from '../../assets/images/img_construction_workers.png';
import aiSectionImage from '../../assets/images/img_leverageai.jpg';
import businessProcessImage from '../../assets/images/imgBusinessProcess.jpg';
import appManImage from '../../assets/images/img_mobileappperson.png'

import BusinessSearchForm from './components/BusinessSearchForm';
import VerifiedBadgeIcon from './components/VerifiedBadgeIcon';



import Header from "./components/Header";
import Footer from "./components/Footer";

const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};



// --- HERO SECTION (Left Aligned, Dark Blue, Background Image) ---
const NewHeroSection = () => {
  return (
    <section 
      className="bs-hero-section" 
      style={{ backgroundImage: `url(${businessHeroImage})` }}
    >
      <div className="bs-hero-overlay"></div>
      <div className="bs-hero-content">
        <div className="bs-hero-text-block">
            <div className="bs-rating-badge">
              <div className="bs-stars">
                <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
              </div>
              <span>4.9 Rated, 200+ Reviews</span>
            </div>
            <h1 className="bs-hero-title">
              LET QUALITY LEADS<br />COME TO YOU
            </h1>
            <p className="bs-hero-subtitle">
              BuildStart is an invitation only directory of the most trusted service providers in your area. 
              We connect you with serious homeowners and like minded professionals, ready to hire.
            </p>
            
            {/* Business Specific Search/CTA Form */}
            <BusinessSearchForm />


        </div>
      </div>
    </section>
  );
};

// --- CONNECT SECTION (Blue Banner, Workers on Left) ---
const ConnectSection = () => {
  return (
    <section className="bs-connect-section">
      <div className="bs-container">
        <div className="bs-connect-grid">
          <div className="bs-connect-image-col">
            <img src={workersImage} alt="Trade Professionals" className="bs-connect-img"/>
          </div>
          <div className="bs-connect-content-col">
            <h2 className="bs-connect-title">
              CONNECT WITH MORE<br />
              <span className="bs-font-thin">LOCAL CLIENTS</span>
            </h2>
            <p className="bs-connect-desc">
              List your business at Building Start and get leads for jobs Posted by local clients.
            </p>
            <div className="bs-btn-group">
              <Link to="/select-role" className="bs-btn-pill-primary">List your Business Today</Link>
              <button className="bs-btn-pill-outline" onClick={() => scrollToSection('how-it-works')} >Learn More</button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- AI SECTION (Left Aligned, Short Image) ---
const AISection = () => {
  return (
    <section className="bs-ai-section">
      <div className="bs-container-grid">
        <div className="bs-image-col">
          <img src={aiSectionImage} alt="AI Powered Platform" className="bs-rounded-img"/>
        </div>
        <div className="bs-content-col">
          <h2>
            LEVERAGE THE<br />
            <span className="bs-text-primary">POWER OF</span>
            <span className="bs-ai-badge">AI</span>
          </h2>
          <p>
            Experience the industry's first AI Powered platform to
            promote your business, match you with most relevant
            clients and help manage your workflows and jobs.
          </p>
        </div>
      </div>
    </section>
  );
};

const VerifiedBadgeSection = () => {
  return (
    <section className="bs-verified-section">
      <div className="bs-container">
        <div className="bs-verified-wrapper">
          
          {/* Left Side: The Badge */}
          <div className="bs-verified-image-col">
             <VerifiedBadgeIcon size="64" />
          </div>

          {/* Right Side: The Copy */}
          <div className="bs-verified-content-col">
            <h2 className="bs-verified-title">
              SEPARATE YOURSELF FROM THE <span className="bs-text-danger">REST</span>
            </h2>
            <h3 className="bs-verified-subtitle">
              Earn the badge that builds instant trust.
            </h3>
            <p className="bs-verified-desc">
              Obtaining the <strong>"Verified Pro"</strong> badge signals to every lead that you have 
              passed rigorous documentation and licensing checks. This specialized status 
              distinguishes you from unverified competitors, helping you win more work 
              and attract premium users who value quality over the lowest price.
            </p>
            
            {/* UPDATED: Wrapped in right-align container */}
            <div className="bs-verified-btn-wrapper">
                <a href="/select-role" className="bs-btn-verified-pill">
                  Get Verified Today
                </a>
            </div>

          </div>

        </div>
      </div>
    </section>
  );
};



// --- HOW IT WORKS SECTION ---
const HowItWorksSection = () => {

const businessSteps = [
  { 
    number: '01', 
    title: 'Join the Elite & Get Verified', 
    desc: 'Register for our invitation-only directory. Upload your credentials to become a "Verified Pro"' 
  },
  { 
    number: '02', 
    title: 'Let AI Qualify Your Leads', 
    desc: 'Our AI Job Assistant pre-vets homeowners, capturing budgets and site details. You receive detailed leads via email and SMS.' 
  },
  { 
    number: '03', 
    title: 'Quote in Seconds', 
    desc: 'Use our pre-built quote templates to submit professional estimates instantly. Be the first to quote and increase your win rate.' 
  },
  { 
    number: '04', 
    title: 'Win, Manage & Scale', 
    desc: 'Secure jobs and manage projects in one dashboard. Need extra hands? Subcontract work to other Verified Pros in our network to grow your empire.' 
  },
];

  return (
    <section id="how-it-works" className="bs-how-section">
      <div className="bs-container-grid">
        
        {/* TEXT COLUMN */}
        <div className="bs-content-col">
          <h2>How It Works</h2>
          <p className="bs-subtitle">Here is how Build Start workds for you:</p>
          
          <div className="bs-steps-list">
            {businessSteps.map((step, index) => (
              <div key={index} className="bs-step-item">
                <div className="bs-step-number">{step.number}</div>
                <div className="bs-step-content">
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* IMAGE COLUMN (Using your existing code from previous steps) */}
        <div className="bs-image-col">
          <div className="bs-image-wrapper-styled">
             <img 
                src={businessProcessImage} 
                alt="Business Process" 
                className="bs-process-img" 
             />
          </div>
        </div>

      </div>
    </section>
  );
};


const PricingSection = () => (
    <section id="pricing" className="bs-pricing-section">
        <div className="bs-container">
             <h2 className="bs-section-title-center">Plans Designed for Growth</h2>
             <p className="bs-subheadline-center">Start your <strong>30-Day Free Trial</strong> on the Pro plan</p>
             
             {/* PRICING CARDS */}
             <div className="bs-pricing-grid">
                
                {/* Starter */}
                <div className="bs-pricing-card">
                    <h3>Starter Pack</h3>
                    <div className="bs-price">$99<span>/mo</span></div>
                    <ul className="bs-pricing-features">
                        <li><FaCheck className="bs-icon-check"/> <strong>Instant</strong> Searchable Listing</li>
                        <li><FaCheck className="bs-icon-check"/> Unlimited Job Leads</li>
                        <li><FaCheck className="bs-icon-check"/> "Verified Pro" Badge</li>
                        <li><FaCheck className="bs-icon-check"/> Customer Contact Details</li>
                        <li><FaCheck className="bs-icon-check"/> Email & App Notifications</li>
                    </ul>
                    <Link to="/register/business?plan=starter" className="bs-btn-pricing-outline">Start Free Trial</Link>
                </div>

                 {/* Pro (Highlighted) */}
                 <div className="bs-pricing-card bs-featured-plan">
                    <div className="bs-popular-badge">Most Popular</div>
                    <h3>Pro Pack</h3>
                    <div className="bs-price">$140<span>/mo</span></div>
                    <ul className="bs-pricing-features">
                        <li><FaCheck className="bs-icon-check"/> <strong>Everything in Starter</strong></li>
                        <li><FaCheck className="bs-icon-check"/> <strong>Priority Leads</strong> (1 hr early)</li>
                        <li><FaCheck className="bs-icon-check"/> "Verified Pro" Badge</li>
                        <li><FaCheck className="bs-icon-check"/> 4 x Sponsored Social Posts</li>
                        <li><FaCheck className="bs-icon-check"/> 4 x Social Video Posts</li>
                    </ul>
                    <Link to="/register/business?plan=pro" className="bs-btn-pricing-primary">Start Free Trial</Link>
                </div>

                 {/* Corporate */}
                 <div className="bs-pricing-card">
                    <h3>Corporate Pack</h3>
                    <div className="bs-price">$999<span>/mo</span></div>
                    <ul className="bs-pricing-features">
                        <li><FaCheck className="bs-icon-check"/> <strong>Everything in Pro</strong></li>
                        <li><FaCheck className="bs-icon-check"/> Featured on Home Page</li>
                        <li><FaCheck className="bs-icon-check"/> Top of Search Results</li>
                        <li><FaCheck className="bs-icon-check"/> Unlimited Sponsored Posts</li>
                        <li><FaCheck className="bs-icon-check"/> Account Manager</li>
                    </ul>
                    <Link to="/register/business?plan=corporate" className="bs-btn-pricing-outline">Start Free Trial</Link>
                </div>
            </div>

            {/* --- COMPARISON TABLE --- */}
            <div className="bs-comparison-wrapper">
                <h3 className="bs-section-divider">Compare Features</h3>
                <div className="bs-table-responsive">
                    <table className="bs-comparison-table">
                        <thead>
                            <tr>
                                <th style={{textAlign:'left'}}>Feature</th>
                                <th>Basic (Free)</th>
                                <th className="bs-col-highlight">Starter / Pro</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Directory Listing</td>
                                <td className="bs-text-muted">Bottom of List</td>
                                <td className="bs-text-bold">High Visibility</td>
                            </tr>
                            <tr>
                                <td>Job Leads</td>
                                <td className="bs-text-muted"><FaTimes/> None</td>
                                <td className="bs-text-success"><FaCheck/> Unlimited</td>
                            </tr>
                            <tr>
                                <td>View Contact Details</td>
                                <td className="bs-text-muted"><FaTimes/> Locked</td>
                                <td className="bs-text-success"><FaCheck/> Unlocked</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {/* --- PAY AS YOU GO --- */}
            <div className="bs-payg-section">
                <h3 className="bs-section-divider">Or Pay As You Go</h3>
                <div className="bs-payg-card">
                    <div className="bs-payg-content">
                        <h3><strong>Advertising Boosts</strong></h3>
                        <p>Purchase one-off advertising boosts on our Social Media Platform.</p>
                    </div>
                    <ul className="bs-payg-list">
                        <li>
                            <span>Sponsored Image Ad</span>
                            <strong>$7.70</strong>
                        </li>
                        <li>
                            <span>Sponsored Video Ad</span>
                            <strong>$9.90</strong>
                        </li>
                    </ul>
                    <div className="bs-payg-action">
                         <Link to="/register/business" className="bs-btn-pricing-outline">Get Started</Link>
                    </div>
                </div>
                <p className="bs-footnote">* Ads are valid for one month.</p>
            </div>
        </div>
    </section>
);


{/*
// --- APP DOWNLOAD SECTION ---
const AppDownloadSection = () => {
  return (
    <section className="bs-app-section">
      <div className="bs-container">
        <div className="bs-app-row">
          
          
          <div className="bs-app-content-left">
            <h2>
              Download the Build Start<br />
              app <span className="bs-text-highlight">to stay connected</span>
            </h2>
            <p className="bs-app-desc">Get a better view of what you both want.</p>
            
            <div className="bs-app-buttons-row">
              <button className="bs-store-btn-black">
                <FaApple size={28} />
                <div className="bs-btn-text">
                  <span className="small">Download on the</span>
                  <span className="large">App Store</span>
                </div>
              </button>
              
              <button className="bs-store-btn-black">
                <FaGooglePlay size={24} />
                <div className="bs-btn-text">
                  <span className="small">GET IT ON</span>
                  <span className="large">Google Play</span>
                </div>
              </button>
            </div>
          </div>

          
          <div className="bs-app-image-wrapper">
             <img src={appManImage} alt="App User" className="bs-app-man-img" />
          </div>

        </div>
      </div>
    </section>
  );
};

// --- TESTIMONIALS SECTION ---
const TestimonialsSection = () => {
  const testimonials = [
    {
      id: 1,
      name: "James H.",
      role: "Plumbing Business Owner",
      quote: "BuildStart has been a game changer. We used to rely on word of mouth, but now 40% of our jobs come directly from the app. The verified badge definitely helps win trust.",
      avatar: "https://i.pravatar.cc/150?u=1"
    },
    {
      id: 2,
      name: "Sarah Jenkins",
      role: "Homeowner",
      quote: "I was terrified of hiring a 'cowboy' builder for my extension. Using BuildStart allowed me to check credentials instantly. Found a fantastic team in 24 hours.",
      avatar: "https://i.pravatar.cc/150?u=2"
    },
    {
      id: 3,
      name: "Michael Chen",
      role: "Property Developer",
      quote: "As a developer, I need reliability. I use the platform to source electricians and surveyors for multiple sites. It cuts my admin time in half.",
      avatar: "https://i.pravatar.cc/150?u=3"
    },
    {
      id: 4,
      name: "Emma W.",
      role: "Homeowner",
      quote: "Simple, fast, and transparent. I posted a job for a bathroom reno and got three quotes from verified pros within the same day. Highly recommended.",
      avatar: "https://i.pravatar.cc/150?u=4"
    },
    {
      id: 5,
      name: "David Ross",
      role: "Electrician",
      quote: "The lead quality is much higher here than on other directories. I'm not fighting with 20 other people for a tyre-kicker. The clients here are ready to hire.",
      avatar: "https://i.pravatar.cc/150?u=5"
    }
  ];

  return (
    <section className="bs-testimonials-section">
      <div className="bs-container">
        
        <div className="bs-testimonials-header">
           <div className="bs-header-stars">
              <FaStar /><FaStar /><FaStar /><FaStar /><FaStar /> 
              <span>4.9 Rated, 230+ Reviews</span>
           </div>
          <h2>HEAR WHAT THOUSANDS<br />OF OUR USERS THINK ABOUT US</h2>
          <p className="bs-testimonials-subtitle">
            Read about experiences of our users and see how we can connect you with the TOP Tradies in Town.
          </p>
        </div>

        
        <div className="bs-testimonials-scroller">
           {testimonials.map((t) => (
             <div key={t.id} className="bs-testimonial-card">
               <div className="bs-card-top">
                 <FcGoogle size={24} /> 
                 <div className="bs-card-stars">
                    <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                 </div>
               </div>
               
               <p className="bs-quote">"{t.quote}"</p>
               
               <div className="bs-author">
                 <img src={t.avatar} alt={t.name} className="bs-avatar" />
                 <div className="bs-author-info">
                   <strong>{t.name}</strong>
                   <span>{t.role}</span>
                 </div>
               </div>
             </div>
           ))}
        </div>

        
        <div className="bs-scroller-dots">
            <span></span><span className="active"></span><span></span>
        </div>

      </div>
    </section>
  );
};


const FinalCtaSection = () => (
    <section className="bs-final-cta-section">
        <div className="bs-container">
            <div className="bs-final-cta-content">
                <h2 className="bs-cta-title">
                    READY TO SCALE YOUR <span className="bs-text-gold-gradient">BUSINESS?</span>
                </h2>
                <p className="bs-cta-subtitle">
                    Join the invitation-only network where quality work is rewarded. 
                    Stop chasing leads and start building your empire.
                </p>
                <div className="bs-cta-btn-wrapper">
                    <Link to="/select-role" className="bs-btn-cta-pill">
                        Create Your Free Profile <FaArrowRight />
                    </Link>
                </div>
                <p className="bs-cta-note">No credit card required for trial.</p>
            </div>
        </div>
    </section>
);

*/}

// --- MAIN PAGE COMPONENT ---
const BusinessGrowthPage = () => {
    return (
        <div className="bs-new-layout">
            
            {/* 1. REAL SOCIAL PROOF COMPONENT */}
            <SocialProofEngine />
            
            <Header />
            <main>
                <NewHeroSection />
                <ConnectSection />

                <VerifiedBadgeSection />

                <div className="bs-jobs-wrapper-full">
                    <AvailableJobs />
                </div>

                <AISection />

                <HowItWorksSection />
                <PricingSection />

                <AppDownloadSection />
                <TestimonialsSection />
                
              <FinalCtaSection 
                  title="READY TO SCALE YOUR " 
                  highlighttext="BUSINESS?" 
                  subtitle=" Join the invitation-only network where quality work is rewarded. 
                    Stop chasing leads and start building your empire." 
                />


            </main>
            <Footer />
            
            {/* 3. REAL AI ASSISTANT COMPONENT */}
            <AiSearchAssistant />
        </div>
    );
};

export default BusinessGrowthPage;
